import { Component, ElementRef, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators, FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { forkJoin, Observable, OperatorFunction, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, filter, startWith } from 'rxjs/operators';
import { browserRefresh } from '@app/app.component';

import * as moment from 'moment';

import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';

import { InvoiceClaimService } from '../services/invoice-claim.service';
import { SharedService, LoadingService } from '@core/shared';

@UntilDestroy()
@Component({
  selector: 'app-invoice-claim-submission-v2',
  templateUrl: './invoice-claim-submission-v2.component.html',
  styleUrls: ['./invoice-claim-submission-v2.component.scss']
})
export class InvoiceClaimSubmissionV2Component implements OnInit, OnDestroy {

  navigationStartSubscription: Subscription;
  languageChangeSubscription: Subscription;
  browserRefresh: boolean;
  selectedCulture: any;

  claimFormId: string;
  claimForm: any;
  attributesDetails: any[] = [];
  lineItemAttributesDetails: any[] = [];
  products: any[] = [];
  invoiceSubmissionForm: FormGroup;
  isProcessingSubmission:boolean = false;
  success: boolean;
  submitted: boolean;
  maxSaleDate: any;
  minSaleDate: any;
  extensions: string[] = [];
  fileErrorMessage: string;
  files: any[] = [];
  duplicateError = '';
  @ViewChild('fileUpload') fileUpload: ElementRef;

  instructions: string;
  helpTitle: string;
  defaultProductId = '48158E6A-AC7E-4065-A2F5-B47800C78096';

  constructor(private fb: FormBuilder, private route: ActivatedRoute, private router: Router, private invoiceClaimService: InvoiceClaimService,
    private sharedService: SharedService, public loadingService: LoadingService, private translate: TranslateService) { }

  ngOnInit(): void {
    this.navigationStartSubscription = this.router.events.pipe(
      filter((event: RouterEvent) => event instanceof NavigationEnd),
      startWith('Initial call')
    ).subscribe((res) => {
      this.selectedCulture = localStorage.getItem('culture');
      if (this.claimFormId != this.route.snapshot.params.id) {
        this.browserRefresh = browserRefresh;
        this.claimFormId = this.route.snapshot.params.id;
        this.init();
      }
    });

    this.languageChangeSubscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.selectedCulture = localStorage.getItem('culture');
      if (!this.browserRefresh)
        this.init();
      else
        this.browserRefresh = false;
    });
  }

  init() {
    let todayDate = new Date();
    this.maxSaleDate = { year: todayDate.getFullYear(), month: todayDate.getMonth() + 1, day: todayDate.getDate() };
    this.claimFormId = this.route.snapshot.params.id;
    let claimConfiguration = this.sharedService.getClaimConfiguration(this.claimFormId);
    let invoiceClaimFields = this.invoiceClaimService.getInvoiceClaimFields(this.claimFormId);
    forkJoin([claimConfiguration, invoiceClaimFields]).subscribe(response => {
      this.claimForm = response[0];
      this.attributesDetails = response[1].headerFields;
      this.lineItemAttributesDetails = response[1].lineItemFields;

      this.setupUserGuide(this.claimForm);

      this.extensions = this.claimForm.fileTypes.split(",");
      let date = new Date();
      let minDate = new Date(date.setDate(date.getDate() - this.claimForm.daysAllowedToClaimSale));
      this.minSaleDate = { year: minDate.getFullYear(), month: minDate.getMonth() + 1, day: minDate.getDate() };
      this.getProducts(this.claimForm.productMasterId);
      this.loadForm();
    });
  }

  setupUserGuide(claimConfiguration) {
    this.instructions = '';
    claimConfiguration.instructions.forEach(instruction => {
      if (instruction.instruction && instruction.culture === this.selectedCulture) {
        this.instructions = instruction.instruction;
        this.helpTitle = instruction.title;
      }
    });
  }


  getProducts(productMasterId) {
    this.invoiceClaimService.getProducts(productMasterId, this.claimFormId).subscribe(response => {
      this.products = response;
    })
  }

  loadForm() {
    let formData = {
      claimConfigurationId: this.claimFormId,
      invoiceNumber: [null, Validators.required],
      dateOfSale: [this.maxSaleDate, Validators.required],
      attributes: this.fb.group({}),
      salesLineItems: this.fb.array([])
    };
    // v2: attributesDetails not used in this component
    this.invoiceSubmissionForm = this.fb.group(formData);
    this.addNewLineItem();
  }

  addNewLineItem() {
    const salesLineItems = this.invoiceSubmissionForm.get('salesLineItems') as FormArray;
    let lineItemGroup = this.fb.group({
      product: new FormControl(this.defaultProductId),
      productName: new FormControl(null),
      quantity: new FormControl(0, this.getQuantityValidation()),
      value: new FormControl(0, this.getValueValidation()),
      attributeJson: new FormControl('{}'),
      additionalAttributes: this.fb.group({})
    });
    // v2: lineItemAttributesDetails not used in this component
    salesLineItems.push(lineItemGroup);
  }

  getQuantityValidation() {
    let validators = null;
    if (this.claimForm.isQuantityEnabled) {
      validators = Validators.compose([Validators.required, Validators.min(0), Validators.max(10000000)])
    }
    return validators;
  }

  getValueValidation() {
    let validators = null;
    if (this.claimForm.isValueEnabled) {
      validators = Validators.compose([Validators.required, Validators.min(0), Validators.max(10000000)])
    }
    return validators;
  }

  validateProduct(fieldControl: FormControl) {
    if (!fieldControl.value) {
      return null;
    }
    let product = this.products.find(p => p.externalIdentifier.toLowerCase() === fieldControl.value.toLowerCase());
    return product ? null : {
      notEqual: true
    };
  }

  checkAttributesValidation(key) {
    return this.invoiceSubmissionForm.get(`attributes.${key}`).errors;
  }

  deleteLineItem(index: number) {
    const formSalesLineItems = this.invoiceSubmissionForm.get('salesLineItems') as FormArray;
    formSalesLineItems.removeAt(index)
  }

  search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(debounceTime(50), distinctUntilChanged(), map(term => term.length < 2 ? []
      : this.products.map(x => x.externalIdentifier).filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10))
    )

  onSubmit() {
   
    this.duplicateError = '';
    this.submitted = true;
    if (this.files.length > 0 && !this.checkFileValidation() && this.invoiceSubmissionForm.valid) {
      
     this.isProcessingSubmission = true;
      let payload = this.invoiceSubmissionForm.getRawValue();
      if (payload.claimConfigurationId == null) {
        payload.claimConfigurationId = this.claimFormId;
      }
      let attributes = []
      this.attributesDetails.forEach(x => {
        let attribute = {};
        attribute['name'] = x.source;
        attribute['dataType'] = x.dataType;
        attribute['sortOrder'] = x.sortOrder;
        attribute['valueAsString'] = x.dataType.name === 'Date' ? moment(payload.attributes[x.destination], "DD-MM-YYYY").format("YYYY-MM-DD") + "T00:00:00" : payload.attributes[x.destination];
        attribute['isKey'] = x.isKey;
        attributes.push(attribute);
      });

      delete payload.attributes;
      payload['attributes'] = attributes;

      payload.salesLineItems.forEach(item => {
        const product = item.productName
          ? this.products.find(p => p.externalIdentifier.toLowerCase() === item.productName.toLowerCase())
          : null;
        if (product)
          item.product = product.id;
        if (item.quantity === null)
          item.quantity = 0;
        if (item.value === null)
          item.value = 0;

        let additionalAttributes = [];
        this.lineItemAttributesDetails.forEach(x => {
          let additionalAttribute = {};
          additionalAttribute['name'] = x.source;
          additionalAttribute['dataType'] = x.dataType;
          additionalAttribute['sortOrder'] = x.sortOrder;
          additionalAttribute['valueAsString'] = x.dataType.name === 'Date' ? moment(item.additionalAttributes[x.destination], "DD-MM-YYYY").format("YYYY-MM-DD") + "T00:00:00" : item.additionalAttributes[x.destination];
          additionalAttribute['isKey'] = x.isKey;
          additionalAttributes.push(additionalAttribute);
        });

        delete item.additionalAttributes;
        item['additionalAttributes'] = additionalAttributes;
      });

      const ds = payload.dateOfSale;
      payload.dateOfSale = (ds && typeof ds === 'object')
        ? `${ds.year}-${String(ds.month).padStart(2, '0')}-${String(ds.day).padStart(2, '0')}T00:00:00`
        : moment(ds, "DD-MM-YYYY").format("YYYY-MM-DD") + "T00:00:00";

      this.loadingService.doLoading(this.invoiceClaimService.invoiceSubmission(payload), this)
        .pipe(untilDestroyed(this)).subscribe({
          next: (response) => {
            
            let invoiceId = response.invoiceId;
            this.uploadInvoices(invoiceId);
            // this.isProcessingSubmission = false;
          },
          error: (error) => {
            this.handleError(error)
            this.isProcessingSubmission = false;
          }
        });
    }
    
  }

  handleError(error) {
    if (error.error && Array.isArray(error.error)) {
      for (let lineItem of error.error) {
        for (let key in lineItem) {
          if (key != 'external sales Identifier(unique)')
            this.duplicateError = `${this.duplicateError} ${key} = ${lineItem[key]} `;
        }
        this.duplicateError = `${this.duplicateError} </br>`;
      }
    }
  }

  uploadInvoices(invoiceId) {
    this.loadingService.doLoading(this.invoiceClaimService.uploadInvoices(invoiceId, this.files), this)
      .pipe(untilDestroyed(this)).subscribe({
        next: () => {
          this.clearForm();
          this.success = true;
          setTimeout(() => {
            this.success = false;
          }, 6000);
          this.submitted = false;
        },
        error: () => {
          this.submitted = false;
        }
      });
  }

    fileHandler(fileList: FileList) {
        this.fileErrorMessage = null;
        const filesArray = Array.from(fileList);
        if (filesArray.length > 0) {
            filesArray.forEach((file) => {
                if (!this.files.find((x) => x.name === file.name)) {
                    this.files.push(file);
                }
            });
        }
        if (this.fileUpload.nativeElement) {
            this.fileUpload.nativeElement.value = null;
        }
    }


  removeFile(file) {
    let index = this.files.indexOf(file);
    if (index != -1)
      this.files.splice(index, 1);
  }

  getExtension(filename) {
    var parts = filename.split('.');
    return parts[parts.length - 1];
  }

  checkFileValidation() {
    this.fileErrorMessage = null;
    let hasError = false;
    for (let file of this.files) {
      if (hasError) break;

      let extension = this.getExtension(file.name);
      if (!this.extensions.includes(`.${extension}`)) {
        hasError = true;
        this.fileErrorMessage = this.translate.instant('InvoiceClaimPage.InvoiceClaimSubmission.FileTypeInvalid');
        break;
      }

      if (this.files.length > this.claimForm.maxNoOfFiles) {
        hasError = true;
        this.translate.get('InvoiceClaimPage.InvoiceClaimSubmission.FileNumberInvalid', { number: this.claimForm.maxNoOfFiles }).subscribe((res) => {
          this.fileErrorMessage = res;
        });
        break;
      }
      let sizeConverter;
      if (this.claimForm.fileSizeMetrics == "mb") {
        sizeConverter = 1023 * 1023;
      }
      else {
        sizeConverter = 1023;
      }

      if ((file.size / sizeConverter) > this.claimForm.maxFileSize) {
        hasError = true;
        this.translate.get('InvoiceClaimPage.InvoiceClaimSubmission.FileSizeInvalid', { size: this.claimForm.maxFileSize }).subscribe((res) => {
          this.fileErrorMessage = res;
        });
        break;
      }
    }
    return hasError;
  }

  clearForm() {
    this.invoiceSubmissionForm.reset();
    const formSalesLineItems = this.invoiceSubmissionForm.get('salesLineItems') as FormArray;
    while (formSalesLineItems.length !== 0) {
      formSalesLineItems.removeAt(0)
    }
    this.addNewLineItem();
    this.files = [];
    if (this.fileUpload.nativeElement)
      this.fileUpload.nativeElement.value = null;
      this.isProcessingSubmission = false;
  }

  isShownDiv: boolean = false; // hidden by default
  toggleShows() {
    this.isShownDiv = !this.isShownDiv;
  }

  ngOnDestroy() {
    if (this.languageChangeSubscription)
      this.languageChangeSubscription.unsubscribe();
    if (this.navigationStartSubscription)
      this.navigationStartSubscription.unsubscribe()
  }

}
